import React, { useState, useRef, useEffect } from 'react'
import { motion, PanInfo, useMotionValue } from "framer-motion"
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'

interface FooterProps {
  currentStep: number
}

export function Footer({ currentStep }: FooterProps) {
  const { t } = useTranslation();
  const handleWhatsAppClick = () => {
    window.open('https://wa.me/+212608112626', '_blank')
  }

  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const buttonRef = useRef<HTMLDivElement>(null)
  const [constraints, setConstraints] = useState({ top: 0, right: 0, bottom: 0, left: 0 })
  const [isBubbleVisible, setIsBubbleVisible] = useState(true)

  useEffect(() => {
    const updateConstraints = () => {
      if (buttonRef.current) {
        const { width, height } = buttonRef.current.getBoundingClientRect()
        setConstraints({
          top: 0,
          right: window.innerWidth - width,
          bottom: window.innerHeight - height,
          left: 0
        })
      }
    }

    updateConstraints()
    window.addEventListener('resize', updateConstraints)

    const savedPosition = localStorage.getItem('whatsappButtonPosition')
    if (false || savedPosition) {
      const { x: savedX, y: savedY } = JSON.parse(savedPosition)
      x.set(Math.max(0, Math.min(savedX, window.innerWidth - 60)))
      y.set(Math.max(0, Math.min(savedY, window.innerHeight - 60)))
    } else {
      // Default position if not saved
      x.set(window.innerWidth - 80)
      y.set(window.innerHeight + 30)
    }

    return () => window.removeEventListener('resize', updateConstraints)
  }, [])

  const onDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const newX = Math.max(0, Math.min(x.get() + info.offset.x, constraints.right))
    const newY = Math.max(0, Math.min(y.get() + info.offset.y, constraints.bottom))
    x.set(newX)
    y.set(newY)
    localStorage.setItem('whatsappButtonPosition', JSON.stringify({ x: newX, y: newY }))
  }

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200">
        <div className="flex justify-around items-center py-3 px-4 max-w-md mx-auto relative">
          {/* Connecting lines */}
          <div className="absolute top-1/2 left-[60px] right-[60px] h-[2px] bg-gray-200 -translate-y-1/2">
            <div 
              className="h-full bg-[#368fc4] transition-all duration-300"
              style={{
                maxWidth:"100%",
                width: `${Math.max(0, ((currentStep - 1) / 4) * 100)}%`
              }}
            />
          </div>
          
          <FooterItem 
            icon="1"
            isActive={currentStep === 1} 
            hasCompleted={currentStep > 1}
          />
          <FooterItem 
            icon="2"
            isActive={currentStep === 2} 
            hasCompleted={currentStep > 2}
          />
          <FooterItem 
            icon="3"
            isActive={currentStep === 3} 
            hasCompleted={currentStep > 3}
          />
          <FooterItem 
            icon="4"
            isActive={currentStep === 4} 
            hasCompleted={currentStep > 4}
          />
          <FooterItem 
            icon="5"
            isActive={currentStep === 5} 
            hasCompleted={currentStep > 5}
          />
        </div>
      </nav>

      {/* Floating WhatsApp Button with Chat Bubble */}
      <motion.div
        ref={buttonRef}
        className="fixed z-50"
        style={{ 
          x,
          y,
        }}
        drag
        dragMomentum={false}
        dragConstraints={constraints}
        onDragEnd={onDragEnd}
      >
        {isBubbleVisible && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 10 }}
            transition={{ duration: 0.3 }}
            className="absolute bottom-full right-0 mb-2 px-4 py-2 bg-white rounded-lg shadow-lg text-sm font-medium text-gray-700"
            style={{
              maxWidth: '33vw',
              width: 'max-content',
              whiteSpace: 'normal',
              wordWrap: 'break-word'
            }}
          >
            <button
              onClick={() => setIsBubbleVisible(false)}
              className="absolute top-1 right-1 p-1 rounded-full hover:bg-gray-200 transition-colors"
            >
              <X size={16} color='#e32929' />
            </button>
            {t('common.whatsapp')}
            <div className="absolute bottom-0 right-4 w-2 h-2 bg-white transform rotate-45 translate-y-1/2"></div>
          </motion.div>
        )}
        <motion.button
          className="w-[60px] h-[60px] rounded-full shadow-lg cursor-move"
          style={{ 
            backgroundImage: 'url(/whatsapp.png)',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          animate={{ 
            scale: [1, 1.1, 1],
            transition: {
              repeat: Infinity,
              repeatType: "reverse",
              duration: 1
            }
          }}
          onClick={(e) => {
            e.stopPropagation()
            handleWhatsAppClick()
          }}
        />
      </motion.div>
    </>
  )
}

interface FooterItemProps {
  icon: string
  isActive: boolean
  hasCompleted: boolean
}

function FooterItem({ icon, isActive, hasCompleted }: FooterItemProps) {
  return (
    <div className="flex flex-col items-center gap-1 relative z-10">
      <div className={`
        relative flex items-center justify-center w-14 h-14 rounded-full 
        bg-white shadow-sm border border-gray-100
        ${isActive || hasCompleted ? 'border-[#368fc4]' : 'border-gray-200'}
      `}>
        <div 
          className="w-6 h-6 bg-contain bg-no-repeat bg-center"
          style={{ backgroundImage: `url(/${icon}${isActive || hasCompleted ? '_' : ''}.svg)` }}
        />
      </div>
    </div>
  )
}

